<template>
  <div class="tech-timeline-container">
    <div class="timeline-header" data-aos="fade-up">
      <h2 class="timeline-title">技术成长历程</h2>
      <p class="timeline-subtitle">从C语言到全栈开发的技术之路</p>
    </div>
    
    <div class="timeline-wrapper">
      <div class="timeline-line"></div>
      
      <div 
        v-for="(item, index) in timelineData" 
        :key="index"
        class="timeline-item"
        :class="{ 'timeline-item-right': index % 2 === 1 }"
        :data-aos="index % 2 === 0 ? 'fade-right' : 'fade-left'"
        :data-aos-delay="index * 200"
      >
        <!-- 时间轴节点 -->
        <div class="timeline-node">
          <div class="node-icon" :style="{ backgroundColor: item.color }">
            <span class="code-symbol" v-html="item.symbol"></span>
          </div>
          <div class="node-pulse" :style="{ borderColor: item.color }"></div>
        </div>
        
        <!-- 内容卡片 -->
        <div class="timeline-content">
          <div class="timeline-card" :style="{ borderLeftColor: item.color }">
            <div class="card-header">
              <span class="timeline-year">{{ item.year }}</span>
              <h3 class="timeline-tech">{{ item.tech }}</h3>
            </div>
            
            <div class="card-body">
              <p class="timeline-description">{{ item.description }}</p>
              
              <!-- 技术标签 -->
              <div class="tech-tags">
                <span 
                  v-for="tag in item.tags" 
                  :key="tag"
                  class="tech-tag"
                  :style="{ backgroundColor: item.color + '20', borderColor: item.color }"
                >
                  {{ tag }}
                </span>
              </div>
              
              <!-- 项目经验 -->
              <div v-if="item.projects" class="projects-section">
                <h4 class="projects-title">相关项目</h4>
                <ul class="projects-list">
                  <li v-for="project in item.projects" :key="project">{{ project }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TechTimeline',
  data() {
    return {
      timelineData: [
        {
          year: '2020',
          tech: 'C语言程序设计',
          symbol: '{ }',
          color: '#00599C',
          description: '开始我的编程之旅，学习基础的程序设计思维和算法逻辑。通过C语言掌握了指针、内存管理等底层概念。',
          tags: ['指针操作', '数据结构', '算法基础', '内存管理'],
          projects: ['学生管理系统', '简单计算器', '文件操作程序']
        },
        {
          year: '2021',
          tech: 'Java开发',
          symbol: '☕',
          color: '#ED8B00',
          description: '深入学习面向对象编程，掌握Java核心技术栈。从基础语法到Spring框架，逐步构建企业级应用开发能力。',
          tags: ['面向对象', 'Spring Boot', 'MyBatis', 'Maven'],
          projects: ['在线商城系统', '博客管理平台', 'RESTful API服务']
        },
        {
          year: '2022',
          tech: '前端技术栈',
          symbol: '</>',
          color: '#61DAFB',
          description: '转向全栈开发，学习现代前端技术。从HTML/CSS到Vue.js，掌握了响应式设计和组件化开发思维。',
          tags: ['Vue.js', 'JavaScript ES6+', 'CSS3', 'Webpack'],
          projects: ['个人作品集网站', '数据可视化大屏', '移动端H5应用']
        },
        {
          year: '2023',
          tech: '数据库与后端',
          symbol: '🗄️',
          color: '#336791',
          description: '深入学习数据库设计和后端架构。掌握MySQL、Redis等数据存储技术，以及微服务架构设计。',
          tags: ['MySQL', 'Redis', 'Docker', 'Nginx'],
          projects: ['分布式电商平台', '高并发秒杀系统', '数据分析平台']
        },
        {
          year: '2024',
          tech: '全栈进阶',
          symbol: '⚡',
          color: '#FF6B6B',
          description: '持续学习新技术，关注性能优化和用户体验。探索AI技术在开发中的应用，追求技术的深度和广度。',
          tags: ['Node.js', 'TypeScript', 'AI集成', '性能优化'],
          projects: ['智能客服系统', '实时协作平台', 'AI辅助开发工具']
        }
      ]
    }
  },
  mounted() {
    // 添加时间轴动画效果
    this.initTimelineAnimations()
  },
  methods: {
    initTimelineAnimations() {
      // 监听滚动，为进度条添加动画
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in')
          }
        })
      }, { threshold: 0.3 })
      
      // 观察所有时间轴项目
      this.$nextTick(() => {
        setTimeout(() => {
          // 确保组件已挂载且元素存在
          if (!this.$el) return
          
          const timelineItems = this.$el.querySelectorAll('.timeline-item')
          if (timelineItems && timelineItems.length > 0) {
            timelineItems.forEach(item => {
              // 添加更严格的检查
              if (item && 
                  item.nodeType === Node.ELEMENT_NODE && 
                  typeof item.getBoundingClientRect === 'function') {
                try {
                  observer.observe(item)
                } catch (error) {
                  console.warn('观察时间轴元素时出错:', error)
                }
              }
            })
          }
        }, 200) // 增加延迟时间
      })
    }
  }
}
</script>

<style scoped>
.tech-timeline-container {
  padding: 3rem 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 20px;
  margin: 2rem 0;
  position: relative;
  overflow: hidden;
}

.tech-timeline-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.03) 0%, transparent 70%);
  animation: rotate 30s linear infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.timeline-header {
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
}

.timeline-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.timeline-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin: 0;
}

.timeline-wrapper {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(to bottom, #667eea, #764ba2, #f093fb, #f5576c);
  transform: translateX(-50%);
  border-radius: 2px;
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.3);
}

.timeline-item {
  position: relative;
  margin-bottom: 4rem;
  display: flex;
  align-items: center;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.6s ease;
}

.timeline-item.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.timeline-item-right {
  flex-direction: row-reverse;
}

.timeline-node {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}

.node-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 3;
  border: 4px solid var(--bg-primary);
}

.code-symbol {
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.node-pulse {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 2px solid;
  border-radius: 50%;
  animation: pulse 2s infinite;
  opacity: 0.6;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.3;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}

.timeline-content {
  width: calc(50% - 40px);
  padding: 0 2rem;
}

.timeline-item-right .timeline-content {
  text-align: right;
}

.timeline-card {
  background: var(--bg-secondary);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-left: 4px solid;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.timeline-item-right .timeline-card {
  border-left: none;
  border-right: 4px solid;
}

.timeline-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.6s ease;
}

.timeline-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.timeline-card:hover::before {
  left: 100%;
}

.card-header {
  margin-bottom: 1.5rem;
}

.timeline-year {
  display: inline-block;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 0.3rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.timeline-tech {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0.5rem 0 0 0;
}

.timeline-description {
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.timeline-item-right .tech-tags {
  justify-content: flex-end;
}

.tech-tag {
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid;
  transition: all 0.3s ease;
}

.tech-tag:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.projects-section {
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
}

.projects-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
}

.projects-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.projects-list li {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  position: relative;
  padding-left: 1rem;
}

.projects-list li::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: #667eea;
  font-weight: bold;
}

.timeline-item-right .projects-list li {
  padding-left: 0;
  padding-right: 1rem;
  text-align: right;
}

.timeline-item-right .projects-list li::before {
  right: 0;
  left: auto;
  content: '◂';
}

/* 响应式设计 */
@media (max-width: 768px) {
  .timeline-wrapper {
    padding: 0 1rem;
  }
  
  .timeline-line {
    left: 30px;
  }
  
  .timeline-item,
  .timeline-item-right {
    flex-direction: row;
    margin-left: 60px;
  }
  
  .timeline-node {
    left: 30px;
    transform: translateX(-50%);
  }
  
  .timeline-content {
    width: 100%;
    padding: 0 0 0 2rem;
  }
  
  .timeline-item-right .timeline-content {
    text-align: left;
    padding: 0 0 0 2rem;
  }
  
  .timeline-card {
    padding: 1.5rem;
  }
  
  .timeline-item-right .timeline-card {
    border-left: 4px solid;
    border-right: none;
  }
  
  .timeline-item-right .tech-tags {
    justify-content: flex-start;
  }
  
  .timeline-item-right .projects-list li {
    text-align: left;
    padding-left: 1rem;
    padding-right: 0;
  }
  
  .timeline-item-right .projects-list li::before {
    left: 0;
    right: auto;
    content: '▸';
  }
  
  .timeline-title {
    font-size: 2rem;
  }
  
  .node-icon {
    width: 50px;
    height: 50px;
  }
  
  .code-symbol {
    font-size: 1.2rem;
  }
}

@media (max-width: 576px) {
  .timeline-title {
    font-size: 1.8rem;
  }
  
  .timeline-card {
    padding: 1rem;
  }
  
  .timeline-tech {
    font-size: 1.2rem;
  }
}

/* 夜间模式适配 */
.dark-mode .timeline-card {
  background: var(--bg-secondary);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.dark-mode .timeline-card:hover {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
}

.dark-mode .node-icon {
  border-color: var(--bg-primary);
}
</style>